import React, { useEffect } from 'react';
import I18n from 'i18n-js';
import { formatBid, bumpRatePercentToDecimal } from './bump_helpers';
import {
  MAX_BUMP_AMOUNT_PERCENT,
  MIN_BUMP_AMOUNT_PERCENT,
  BUMP_INCREMENT_AMOUNT_PERCENT,
} from '../shared/constants';
import { RCAlertBox, RCIcon, RCRadioCardGroup } from '@reverbdotcom/cadence/components';
import { RCTextUtility } from '@reverbdotcom/cadence/components/RCText/RCText';
import { withPrefix } from '@reverbdotcom/commons/src/i18n_helpers';
import { PencilSimpleIcon, TrophyIcon } from '@reverbdotcom/cadence/icons/react';

const RATE_VALUE_RECOMMENDED = 'recommended';
const RATE_VALUE_CUSTOM = 'custom';

const t = withPrefix('discovery.bumpModal');

export function RecommendedRadioV4({
  currentBid,
  recommendationAmount,
  handleBidChange,
  showCustomWarning,
  setShowCustomWarning,
  minimumBumpBid,
}: {
  currentBid?: number;
  recommendationAmount: number;
  handleBidChange: (bid: number) => void;
  showCustomWarning: boolean;
  setShowCustomWarning: (show: boolean) => void;
  minimumBumpBid?: number;
}) {
  const [selection, setSelection] = React.useState<string>(
    !!currentBid && currentBid !== bumpRatePercentToDecimal(recommendationAmount) ? RATE_VALUE_CUSTOM : RATE_VALUE_RECOMMENDED,
  );

  const [customBid, setCustomBid] = React.useState<string>(
    String(currentBid ? formatBid(currentBid) : recommendationAmount),
  );

  useEffect(() => {
    if (selection === RATE_VALUE_RECOMMENDED) {
      handleBidChange(bumpRatePercentToDecimal(recommendationAmount));
    }
  }, [recommendationAmount, selection, handleBidChange]);


  function applyCustomBid(raw: string) {
    const value = Number.parseFloat(raw);
    const isValid =
      Number.isFinite(value) &&
      value >= (minimumBumpBid ?? MIN_BUMP_AMOUNT_PERCENT) &&
      value <= MAX_BUMP_AMOUNT_PERCENT &&
      value % BUMP_INCREMENT_AMOUNT_PERCENT === 0;
    setShowCustomWarning(!isValid);
    handleBidChange(Number.isFinite(value) ? bumpRatePercentToDecimal(value) : 0);
  }

  const handleCustomSelectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setCustomBid(next);
    applyCustomBid(next);
  };

  function handleRateChange(event: React.ChangeEvent<HTMLInputElement>) {
    const next = event.target.value;
    setSelection(next);

    if (next === RATE_VALUE_RECOMMENDED) {
      setShowCustomWarning(false);
      handleBidChange(bumpRatePercentToDecimal(recommendationAmount));
    } else {
      applyCustomBid(customBid);
    }
  }

  return (
    <div className="d-flex fx-dir-col">
      <div
        className="bump-modal-top-pick__rate-cards"
      >
        <RCRadioCardGroup
          id="bump-top-pick-rate"
          name="bump-top-pick-rate"
          value={selection}
          onChange={handleRateChange}
          label={t('setRateIteration')}
          visuallyHiddenLabel
          iconPlacement="hidden"
          iconAlignment="top"
        >
          <RCRadioCardGroup.Option
            value={RATE_VALUE_RECOMMENDED}
            label={
              <div className="d-flex fx-align-center gap-2">
                <div className="text-color-success">
                  <RCIcon icon={<TrophyIcon />} size={5} />
                </div>
                <RCTextUtility size="500" weight="bold">
                  {`${recommendationAmount}%`}
                </RCTextUtility>
              </div>
            }
            sublabel={t('topPickRecommendedSublabel')}
          />
          <RCRadioCardGroup.Option
            value={RATE_VALUE_CUSTOM}
            label={
              <div className="d-flex fx-align-center gap-2">
                <RCIcon icon={<PencilSimpleIcon />} size={5} />
                <RCTextUtility size="500" weight="bold">
                  {t('topPickCustomLabel')}
                </RCTextUtility>
              </div>
            }
            sublabel={t('topPickCustomSublabel')}
          />
        </RCRadioCardGroup>
      </div>

      {selection === RATE_VALUE_CUSTOM && (
        <div className="d-flex fx-dir-col mt-3">
          <div className="width-100">
            <div className="input-group">
              <input
                type="number"
                aria-label={t('customRate')}
                className="input-group__input"
                value={customBid}
                onChange={handleCustomSelectChange}
              />
              <div className="input-group__text">%</div>
            </div>
          </div>
          {showCustomWarning && (
            <div className="mt-2">
              <RCAlertBox type="error" small>
                {I18n.t('discovery.bumpModal.customBumpWarning', {
                  minBump: minimumBumpBid ?? MIN_BUMP_AMOUNT_PERCENT,
                  maxBump: MAX_BUMP_AMOUNT_PERCENT,
                  bumpIncrement: BUMP_INCREMENT_AMOUNT_PERCENT,
                })}
              </RCAlertBox>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
